import React from 'react';
import ErrorBoundary from '../shared/ErrorBoundary';
import Accordion from '../shared/Accordion';
import MessageList from '../shared/MessageList';
import { ToolCard, parseDoubleEncoded } from '../shared/ToolCard';
import JSONTreeViewer from '../../../../components/JSONTreeViewer';
import { isJsonMime } from '../../../../utils/mimeType';
import { extractInputOutputMessages } from '../../../../utils/openinference';
import { isLiveKitSpan, extractLiveKitInput } from '../../../../utils/traceSpanIO';
import { palette } from '../../../../utils/designTokens';
import { logger } from '../../../../utils/logger';

/**
 * Check if input value is "empty" (null, empty string, empty object, empty array)
 */
const isEmptyInput = (inputValue) => {
  if (!inputValue) return true;
  if (typeof inputValue === 'string') {
    const trimmed = inputValue.trim();
    return trimmed === '' || trimmed === '{}' || trimmed === '[]';
  }
  if (typeof inputValue === 'object') {
    return Object.keys(inputValue).length === 0;
  }
  return false;
};

/**
 * Extract runtime context/metadata from span attributes.
 * This captures config data like passenger_id that was injected at runtime
 * but not captured as explicit tool arguments.
 */
const extractRuntimeContext = (attributes) => {
  if (!attributes) return null;

  try {
    const attrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;

    // Check for metadata field (contains RunnableConfig data like passenger_id)
    if (attrs.metadata) {
      const metadata = typeof attrs.metadata === 'string'
        ? JSON.parse(attrs.metadata)
        : attrs.metadata;

      // Filter out langgraph internal fields to show only user-relevant context
      const relevantKeys = Object.keys(metadata).filter(key =>
        !key.startsWith('langgraph_') &&
        key !== 'checkpoint_ns'
      );

      if (relevantKeys.length > 0) {
        const relevantMetadata = {};
        relevantKeys.forEach(key => {
          relevantMetadata[key] = metadata[key];
        });
        return relevantMetadata;
      }
    }
  } catch (e) {
    logger.error('Failed to extract runtime context:', e);
  }

  return null;
};

/**
 * InputContent - Renders current input + accordion sections
 */
const InputContent = ({ hasCurrentInput, currentInputMessage, rawInputContent, rawInputIsOnlyContent, toolInputCard, sections, truncatedNotice }) => (
  <div className="flex flex-col h-full">
    {truncatedNotice && (
      <div className="mb-2 px-3 py-2 rounded text-xs italic" style={{ backgroundColor: palette.neutrals.panelSecondary, color: palette.text.muted }}>
        {truncatedNotice}
      </div>
    )}
    {hasCurrentInput && (
      <div className="mb-2">
        <MessageList messages={[currentInputMessage]} type="input" />
      </div>
    )}
    {/* Tool span: always show tool card when available */}
    {!hasCurrentInput && toolInputCard && (
      <div className="mb-2">{toolInputCard}</div>
    )}
    {/* Non-tool raw input as only content */}
    {rawInputIsOnlyContent && !hasCurrentInput && !toolInputCard && rawInputContent}
    {sections.length > 0 && (
      <Accordion sections={sections} nested />
    )}
  </div>
);

const _normalizeMessages = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map(m => {
    const msg = m?.message?.role ? m.message : m?.role ? m : null;
    if (!msg) return null;
    return {
      ...msg,
      role: msg.role === 'model' ? 'assistant' : msg.role,
      content: msg.content ?? msg.parts ?? msg.text ?? '',
    };
  }).filter(Boolean);
};

const _messageHasDisplayContent = (msg) => {
  if (!msg) return false;
  if (typeof msg.content === 'string' && msg.content.trim()) return true;
  if (Array.isArray(msg.content) && msg.content.length > 0) return true;
  if (msg.content && typeof msg.content === 'object') return true;
  return Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0;
};

const InputTab = ({ span, spans = [], spanKind, showRaw = false, drilldownApi = null, drilldownMessages = null, drilldownMessagesLoading = false, drilldownMessagesError = null, fetchDrilldownMessages = null }) => {
  if (!span) {
    throw new Error('InputTab requires span prop');
  }

  logger.debug('InputTab rendering:', { spanKind, spanId: span.span_id, hasInputValue: !!span.input_value });

  const isV2Span = span.__v2Span === true;

  // Check if this is a LiveKit span
  const isLiveKit = isLiveKitSpan(span);

  // Extract and separate messages for LLM spans
  // Structure: [context messages] + [current input (last message)]
  // System prompt is now shown in ConfigurationTab
  let contextMessages = [];
  let currentInputMessage = null;

  if (spanKind === 'llm') {
    if (isV2Span) {
      if (span.user_query) {
        currentInputMessage = { role: 'user', content: span.user_query };
        if (drilldownMessages?.input_messages?.length > 0) {
          const nonSystem = _normalizeMessages(drilldownMessages.input_messages).filter(m => m.role !== 'system');
          const displayable = nonSystem.filter(_messageHasDisplayContent);
          const sourceMessages = displayable.length > 0 ? displayable : nonSystem;
          contextMessages = sourceMessages.filter(m => !(m.role === 'user' && m.content === span.user_query));
        }
      } else if (drilldownMessages?.input_messages?.length > 0) {
        const nonSystem = _normalizeMessages(drilldownMessages.input_messages).filter(m => m.role !== 'system');
        const displayable = nonSystem.filter(_messageHasDisplayContent);
        const sourceMessages = displayable.length > 0 ? displayable : nonSystem;
        if (sourceMessages.length > 0) {
          currentInputMessage = sourceMessages[sourceMessages.length - 1];
          contextMessages = sourceMessages.slice(0, -1);
        }
      }
    } else {
      try {
        const { input: inputMessages } = extractInputOutputMessages(span.attributes);
        if (inputMessages && inputMessages.length > 0) {
          const nonSystemMessages = inputMessages.filter(msg => msg.role !== 'system');

          if (nonSystemMessages.length > 0) {
            // Last message is the current input
            currentInputMessage = nonSystemMessages[nonSystemMessages.length - 1];
            // Everything in between is context
            contextMessages = nonSystemMessages.slice(0, -1);
          }
        }
      } catch (e) {
        logger.error('Failed to extract LLM input messages:', e);
      }
    }
  }

  // Current Input rendered directly (not in accordion)
  const hasCurrentInput = spanKind === 'llm' && currentInputMessage !== null;

  // Build sections array for supporting content in accordion
  const sections = [];

  // Context (LLM only)
  // v2: always reserve the section so the layout is stable while the drilldown fetch is in-flight.
  //     Without this, the section pops in between Input and Output after the fetch completes.
  // v1: context is computed synchronously from attributes — only show when available.
  if (spanKind === 'llm') {
    if (isV2Span) {
      let contextContent;
      if (contextMessages.length > 0) {
        contextContent = <MessageList messages={contextMessages} type="input" />;
      } else if (drilldownMessagesLoading) {
        contextContent = <div className="p-3 text-sm" style={{ color: palette.text.muted }}>Loading…</div>;
      } else {
        contextContent = null;
      }
      sections.push({
        title: contextMessages.length > 0
          ? `Context (${contextMessages.length} message${contextMessages.length !== 1 ? 's' : ''})`
          : 'Context',
        content: contextContent,
        defaultExpanded: false,
        onExpand: fetchDrilldownMessages || undefined,
      });
    } else if (contextMessages.length > 0) {
      sections.push({
        title: `Context (${contextMessages.length} message${contextMessages.length !== 1 ? 's' : ''})`,
        content: <MessageList messages={contextMessages} type="input" />,
        defaultExpanded: false
      });
    }
  }

  // LiveKit Input Sections (v1 only — attribute-parsed)
  if (!isV2Span && isLiveKit) {
    const liveKitSections = extractLiveKitInput(span);
    liveKitSections.forEach((section, idx) => {
      if (section.type === 'text') {
        sections.push({
          title: section.title,
          content: (
            <div
              className="p-3 rounded border font-mono text-sm whitespace-pre-wrap"
              style={{
                backgroundColor: palette.neutrals.canvas,
                borderColor: palette.borders.subtle,
                color: palette.text.primary
              }}
            >
              {section.content}
            </div>
          ),
          defaultExpanded: idx === 0
        });
      } else if (section.type === 'json') {
        const jsonString = typeof section.content === 'string'
          ? section.content
          : JSON.stringify(section.content);
        sections.push({
          title: section.title,
          content: <JSONTreeViewer data={jsonString} />,
          defaultExpanded: idx === 0
        });
      }
    });
  }

  // Raw Input (all span kinds)
  // Priority: show input_value if available, otherwise show messages JSON for LLM spans
  let rawInputData = null;
  let rawInputTitle = 'Raw Input';
  // input.mime_type, returned by the v2 API as a typed column. Previously this was read out of
  // span.attributes and guarded by !isV2Span — but every trace-view span IS v2, and v2 sets
  // attributes:{}, so it could never fire and every plain-text input fell through to the JSON
  // viewer and was reported as "Invalid JSON".
  const inputMimeType = span.input_mime_type || null;

  if (span.input_value) {
    rawInputData = span.input_value;
  } else if (!isV2Span && spanKind === 'llm') {
    // v1 fallback: raw messages JSON from attributes
    try {
      const attrs = typeof span.attributes === 'string'
        ? JSON.parse(span.attributes)
        : span.attributes;

      if (attrs && typeof attrs === 'object') {
        const llmAttrs = attrs.llm || {};
        const inputMessages = llmAttrs.input_messages;

        // Check for nested format first - show entire array as-is
        if (inputMessages && (Array.isArray(inputMessages) ? inputMessages.length > 0 : Object.keys(inputMessages).length > 0)) {
          rawInputData = inputMessages;
          rawInputTitle = 'Raw Input Messages';
        } else {
          // Check for flattened format: extract all llm.input_messages.* keys
          const flattenedMessages = {};
          Object.keys(attrs).forEach(key => {
            if (key.startsWith('llm.input_messages.')) {
              flattenedMessages[key] = attrs[key];
            }
          });

          if (Object.keys(flattenedMessages).length > 0) {
            rawInputData = flattenedMessages;
            rawInputTitle = 'Raw Input Messages';
          }
        }
      }
    } catch (e) {
      logger.error('Failed to extract raw input messages:', e);
    }
  }

  let runtimeContext = null;
  if (!isV2Span && spanKind === 'tool') {
    runtimeContext = extractRuntimeContext(span.attributes);
  }

  // Raw input data is available via toggle, not as a separate accordion section
  // For tool spans with no formatted content, raw input shows directly (no toggle needed)
  const rawInputIsOnlyContent = !hasCurrentInput && sections.length === 0 && rawInputData && !runtimeContext;

  // Runtime Context (for TOOL spans)
  // Shows metadata injected via RunnableConfig (e.g., passenger_id)
  // Can appear alongside explicit arguments or alone
  if (runtimeContext) {
    sections.push({
      title: 'Runtime Context',
      content: (
        <div>
          <JSONTreeViewer
            data={JSON.stringify(runtimeContext)}
            initialExpandDepth={2}
            showControls={false}
          />
          <div className="text-xs text-text-muted mt-2 italic">
            Context injected through runtime configuration
          </div>
        </div>
      ),
      defaultExpanded: true
    });
  }

  // When showRaw is true, render raw content directly (used by OutlineTab toggle)
  if (showRaw) {
    const rawContent = rawInputData ? (() => {
      // Text unless the span declares JSON. A missing mime renders as text, never as an error.
      if (!isJsonMime(inputMimeType) && typeof rawInputData === 'string') {
        return (
          <div
            className="p-3 rounded border font-mono whitespace-pre-wrap break-words"
            style={{ fontSize: '12px', backgroundColor: palette.neutrals.canvas, borderColor: palette.borders.subtle, color: palette.text.primary }}
          >
            {rawInputData}
          </div>
        );
      }
      const jsonString = typeof rawInputData === 'string' ? rawInputData : JSON.stringify(rawInputData);
      return <JSONTreeViewer data={jsonString} mimeType={inputMimeType} truncated={!!span.input_value_truncated} />;
    })() : null;

    return (
      <ErrorBoundary>
        {rawContent || <div className="p-4 text-sm text-text-muted">No raw input data available.</div>}
      </ErrorBoundary>
    );
  }

  // If no content at all, show a message (or drilldown error if that's why)
  if (!hasCurrentInput && !rawInputIsOnlyContent && sections.length === 0) {
    return (
      <ErrorBoundary>
        <div className="p-4 text-sm text-text-muted">
          {drilldownMessagesError
            ? `Failed to load input messages: ${drilldownMessagesError}`
            : 'No input data available for this span.'}
        </div>
      </ErrorBoundary>
    );
  }

  // input_value is a high-risk column the API previews with substring(...) + a truncation
  // flag. A truncated preview is valid data cut mid-JSON, not malformed — surface it as
  // "truncated", not "Invalid JSON".
  const inputValueTruncated = !!span.input_value_truncated;

  // Prepare raw input content for toggle view
  const rawInputContent = rawInputData ? (() => {
    // Text unless the span declares JSON. A missing mime renders as text, never as an error.
    if (!isJsonMime(inputMimeType) && typeof rawInputData === 'string') {
      return (
        <div
          className="p-3 rounded border font-mono whitespace-pre-wrap break-words"
          style={{
            fontSize: '12px',
            backgroundColor: palette.neutrals.canvas,
            borderColor: palette.borders.subtle,
            color: palette.text.primary
          }}
        >
          {rawInputData}
        </div>
      );
    }
    const jsonString = typeof rawInputData === 'string' ? rawInputData : JSON.stringify(rawInputData);
    return <JSONTreeViewer data={jsonString} mimeType={inputMimeType} truncated={inputValueTruncated} />;
  })() : null;

  const inputTruncatedNotice = isV2Span && spanKind === 'llm' && drilldownMessages?.input_messages_truncated
    ? 'Full message history exceeds the display limit. Showing latest user message only.'
    : (inputValueTruncated
        ? 'Input exceeds the display limit. Showing the first part of the raw value.'
        : null);

  return (
    <ErrorBoundary>
      <InputContent
        hasCurrentInput={hasCurrentInput}
        currentInputMessage={currentInputMessage}
        rawInputContent={rawInputContent}
        rawInputIsOnlyContent={rawInputIsOnlyContent}
        toolInputCard={spanKind === 'tool' && (rawInputData || runtimeContext) ? <ToolCard name={span.name || span.span_name || 'Tool'} data={parseDoubleEncoded(span.input_value)} /> : null}
        sections={sections}
        truncatedNotice={inputTruncatedNotice}
      />
    </ErrorBoundary>
  );
};

export default InputTab;
